import React from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { CommentAnchorType } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../tokens'
import { SectionHead } from '../../components/SectionHead'
import { CommentIndicator } from '../../components/CommentIndicator'
import { useUnresolvedCountLookup } from '../UnresolvedCommentsContext'
import { HiddenFromClientChip } from '../../components/HiddenFromClientChip'
import { usePacketSections } from '../PacketSectionsContext'
import { SectionKey } from '../sections'

interface Props {
  palette: SurfacePalette
  sectionKey: SectionKey
  title: string
  muted?: boolean
  /**
   * Suppress the hide control on a card whose visibility is already decided
   * elsewhere — today only the survey card with no Salesforce link, where the
   * derived rule wins outright and a live toggle would silently lose to it.
   */
  hideControl?: 'unavailable'
  /**
   * Drop the section number. For a card Edit shows but Present hides: the slide
   * isn't in the numbered deck, so `useSectionNumber` would fall through to the
   * `'00'` orphan fallback and the header would read "00 Client Survey".
   */
  showNumber?: boolean
  commentAnchor?: { type: CommentAnchorType; id: string; label?: string }
  children?: React.ReactNode
}

export const SectionCard = ({
  palette,
  sectionKey,
  title,
  muted,
  hideControl,
  showNumber = true,
  commentAnchor,
  children,
}: Props) => {
  const countFor = useUnresolvedCountLookup()
  const { numberFor, hide } = usePacketSections()
  const number = numberFor(sectionKey)
  // Present provides no `hide` handle, so nothing below renders there.
  const hideable = hideControl === 'unavailable' ? undefined : hide
  const isHidden = !!hideable?.hidden.has(sectionKey)
  return (
    <section
      id={sectionKey}
      data-section-key={sectionKey}
      data-comment-anchor-type={commentAnchor?.type}
      data-comment-anchor-id={commentAnchor?.id}
      data-comment-label={commentAnchor?.label}
      style={{
        padding: 28,
        borderRadius: radius.lg,
        border: `1px solid ${palette.hairline}`,
        background: muted || isHidden ? palette.bg : palette.surface,
        scrollMarginTop: 16,
        fontFamily: type.family.sans,
        color: palette.text,
      }}
    >
      <SectionHead
        palette={palette}
        // A hidden slide isn't in the numbered deck, so `numberFor` would fall
        // through to the '00' orphan fallback and the header would read "00
        // Brand Standards".
        number={showNumber && !isHidden ? number : undefined}
        title={title}
        right={
          !hideable && !commentAnchor ? undefined : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isHidden && (
                <HiddenFromClientChip
                  palette={palette}
                  title="This slide is skipped in Present. It stays hidden on next sprint's packet until you show it again."
                />
              )}
              {hideable && (
                <button
                  type="button"
                  aria-label={`${isHidden ? 'Show' : 'Hide'} ${title}`}
                  aria-pressed={isHidden}
                  title={
                    isHidden ? 'Show this slide to the client' : 'Hide this slide from the client'
                  }
                  disabled={hideable.pending}
                  onClick={() => hideable.toggle(sectionKey, !isHidden)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    padding: 0,
                    borderRadius: radius.sm,
                    border: `1px solid ${palette.hairline}`,
                    background: 'transparent',
                    color: palette.textMuted,
                    cursor: hideable.pending ? 'default' : 'pointer',
                    opacity: hideable.pending ? 0.5 : 1,
                  }}
                >
                  {isHidden ? (
                    <VisibilityOffIcon style={{ fontSize: 15 }} />
                  ) : (
                    <VisibilityIcon style={{ fontSize: 15 }} />
                  )}
                </button>
              )}
              {commentAnchor ? (
                <CommentIndicator
                  palette={palette}
                  label={title}
                  unresolvedCount={countFor(commentAnchor.type, commentAnchor.id)}
                />
              ) : null}
            </div>
          )
        }
      />
      {children}
    </section>
  )
}
